import { useTheme, Box, InputBase } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { SearchRequest, useConfig } from '../../api/api';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';

const DBSearch = ({
  param,
  onSearch,
  noInitialLoad,
  sort = null,
  children,
  limit = 25,
  sx,
  value,
  hideSearchBar,
  filter,
  className,
  onSearchChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const scrollContainerRef = useRef(null);

  const cookies = new Cookies();
  const prefix = cookies.get('searchPrefix');

  const [search, setSearch] = useState(value || '');
  const [searchResult, setSearchResult] = useState(null);

  // Tracks the query of the most recently issued request so we can drop
  // stale responses (e.g. auto-loaded next page of a query the user has
  // since typed past).
  const latestQueryRef = useRef(value || '');
  // Live copy of the currently rendered hits, used when appending paginated
  // pages. A ref (not the closure) avoids stale-snapshot appends when several
  // requests are in flight at once.
  const hitsRef = useRef([]);
  // The `value` we last kicked a search off for. Lets the [config, value]
  // effect below ignore the echo of our own onSearchChange() call (which would
  // otherwise fire a duplicate request for every keystroke) while still
  // reacting to genuinely external value changes.
  const handledValueRef = useRef(value || '');
  const debounceRef = useRef(null);

  function runSearch(query, newPage = 1, keepHits = false) {
    if (!config) return;

    latestQueryRef.current = query;
    const searchData = {
      q: query,
      ...(sort && !query?.trim() && { sort: [`${sort}:asc`] }),
      page: newPage,
      hitsPerPage: limit,
      filter,
    };
    SearchRequest({
      baseUrl: config.searchUrl,
      path: `indexes/${prefix}${param}/search`,
      method: 'post',
      data: searchData,
      onResponse: (res) => {
        // Drop stale responses (the user has moved on to a newer query).
        if (latestQueryRef.current !== query) return;
        const merged = keepHits ? [...hitsRef.current, ...res.hits] : res.hits;
        hitsRef.current = merged;
        onSearch(merged);
        setSearchResult(res);
      },
    });
  }

  // Debounce input-driven searches so typing doesn't fire a request per
  // keystroke.
  function scheduleSearch(query) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 300);
  }

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  function handleSearch(event) {
    const newValue = event.target.value;
    setSearch(newValue);
    handledValueRef.current = newValue;
    if (onSearchChange) onSearchChange(newValue);
    scheduleSearch(newValue);
  }

  // Initial load and reaction to external `value` changes (e.g. a parent
  // resetting the term when switching tabs). Skipped for our own
  // onSearchChange echo so keystrokes don't double-fire.
  useEffect(() => {
    if (noInitialLoad || !config) return;
    const v = value || '';
    if (v === handledValueRef.current && searchResult) return;
    handledValueRef.current = v;
    setSearch(v);
    runSearch(v);
  }, [config, value]);

  // Re-run the search when the filter changes (e.g. switching project tabs)
  // while preserving the current search term. Skip the initial mount so we
  // don't fire a duplicate of the initial load above.
  const isInitialFilterRun = useRef(true);
  useEffect(() => {
    if (isInitialFilterRun.current) {
      isInitialFilterRun.current = false;
      return;
    }
    if (!noInitialLoad) runSearch(handledValueRef.current);
  }, [filter]);

  return (
    <Box
      ref={scrollContainerRef}
      sx={sx}
      className={
        'relative flex flex-col gap-2 h-full w-full overflow-y-auto pr-2 overflow-x-hidden'
      }
    >
      {!hideSearchBar && (
        <Box
          className="flex backdrop-blur-lg py-1 sticky top-0 z-10"
          borderRadius="3px"
          backgroundColor={colors.bgInput}
        >
          <InputBase
            sx={{ ml: 2, flex: 1, fontSize: '16px' }}
            placeholder={t('Search')}
            value={search}
            onChange={handleSearch}
            className="w-full"
          />
        </Box>
      )}
      {children && searchResult && (
        <InfiniteScroll
          scrollContainerRef={scrollContainerRef}
          searchResult={searchResult}
          onLoadMore={(newPage) =>
            runSearch(latestQueryRef.current, newPage, true)
          }
        >
          {children}
        </InfiniteScroll>
      )}
    </Box>
  );
};

const InfiniteScroll = ({
  children,
  scrollContainerRef,
  searchResult,
  onLoadMore,
  triggerDist = 500,
  className,
}) => {
  // Tracks the page currently being fetched. Cleared on every searchResult
  // update so a fresh search (which resets page back to 1) doesn't leave
  // pagination permanently gated.
  const inFlightPageRef = useRef(null);

  useEffect(() => {
    inFlightPageRef.current = null;
  }, [searchResult]);

  useEffect(() => {
    if (!scrollContainerRef || !scrollContainerRef.current) return;
    const container = scrollContainerRef.current;

    const checkRemaining = (el) =>
      el.scrollHeight - el.clientHeight - el.scrollTop;

    // Find the first descendant that's actually scrolling, if any.
    // Children like ExoTable own their own internal overflow:auto, so the
    // wrapper itself never scrolls and its scrollHeight matches clientHeight.
    const findActiveScroller = () => {
      const queue = [container];
      while (queue.length) {
        const el = queue.shift();
        if (el !== container && el.scrollHeight > el.clientHeight) return el;
        for (const child of el.children) queue.push(child);
      }
      return null;
    };

    const maybeLoadMore = (event) => {
      // On scroll events use the element that actually scrolled (capture
      // phase delivers events from descendants). Otherwise check whichever
      // element in the tree is the real scroller; if nothing is scrollable
      // yet, content doesn't fill the view → load more.
      let remainingScroll;
      if (event && event.target instanceof Element) {
        remainingScroll = checkRemaining(event.target);
      } else {
        // Fall back to the container itself when no descendant scroller
        // exists — in select dialogs the container IS the scroller. Using 0
        // here would make the "fill the view" trigger always fire and walk
        // through every page of the index back-to-back on open.
        const scroller = findActiveScroller() || container;
        remainingScroll = checkRemaining(scroller);
      }

      const totalPages = searchResult.totalPages;
      const currentPage = searchResult.page;

      if (
        triggerDist > remainingScroll &&
        currentPage < totalPages &&
        inFlightPageRef.current === null
      ) {
        inFlightPageRef.current = currentPage + 1;
        onLoadMore(currentPage + 1);
      }
    };

    // Trigger immediately in case content doesn't fill the visible area
    // yet (no scrollbar => scroll event would never fire).
    maybeLoadMore();

    // Capture phase so we catch scroll events on any descendant
    // (scroll events do not bubble).
    container.addEventListener('scroll', maybeLoadMore, true);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => maybeLoadMore());
      resizeObserver.observe(container);
    }

    return () => {
      container.removeEventListener('scroll', maybeLoadMore, true);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [scrollContainerRef, searchResult]);

  return <>{children}</>;
};

export default DBSearch;
